/**
 * Busca de endereço por CEP.
 *
 * Fica em `api/` pela regra do projeto (nenhum `fetch` fora daqui), mas NÃO
 * passa pelo `httpClient`: é serviço público de terceiros, sem cookie de sessão
 * e sem o envelope de erro da nossa API.
 *
 * ViaCEP primeiro, BrasilAPI se o ViaCEP estiver fora — os dois aceitam CORS e
 * não pedem chave. Só o CEP sai do sistema.
 */

export interface EnderecoDoCep {
  logradouro: string;
  bairro: string;
  cidade: string;
  uf: string;
}

/** Teto por provedor: CEP lento demais é pior que preencher à mão. */
const TETO_MS = 5000;

/**
 * `null` = o CEP não existe (o provedor respondeu, mas não achou).
 * Lança quando nenhum provedor respondeu — aí a tela diz "não foi possível
 * buscar", que é diferente de "CEP inexistente".
 */
export async function buscarCep(cep: string, sinal?: AbortSignal): Promise<EnderecoDoCep | null> {
  const digitos = cep.replace(/\D/g, '');
  if (digitos.length !== 8) return null;

  try {
    return await viaCep(digitos, sinal);
  } catch (erro) {
    if (sinal?.aborted) throw erro;
    return brasilApi(digitos, sinal);
  }
}

function comTeto(sinal?: AbortSignal): AbortSignal {
  const teto = AbortSignal.timeout(TETO_MS);
  return sinal ? AbortSignal.any([sinal, teto]) : teto;
}

async function viaCep(cep: string, sinal?: AbortSignal): Promise<EnderecoDoCep | null> {
  const resposta = await fetch(`https://viacep.com.br/ws/${cep}/json/`, { signal: comTeto(sinal) });
  if (!resposta.ok) throw new Error(`ViaCEP respondeu ${resposta.status}`);

  const corpo = (await resposta.json()) as {
    erro?: boolean | string;
    logradouro?: string;
    bairro?: string;
    localidade?: string;
    uf?: string;
  };
  // O ViaCEP responde 200 com `{ "erro": true }` (ou "true") para CEP inexistente.
  if (corpo.erro) return null;

  return {
    logradouro: corpo.logradouro ?? '',
    bairro: corpo.bairro ?? '',
    cidade: corpo.localidade ?? '',
    uf: corpo.uf ?? '',
  };
}

async function brasilApi(cep: string, sinal?: AbortSignal): Promise<EnderecoDoCep | null> {
  const resposta = await fetch(`https://brasilapi.com.br/api/cep/v1/${cep}`, { signal: comTeto(sinal) });
  if (resposta.status === 404) return null;
  if (!resposta.ok) throw new Error(`BrasilAPI respondeu ${resposta.status}`);

  const corpo = (await resposta.json()) as {
    street?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
  };

  return {
    logradouro: corpo.street ?? '',
    bairro: corpo.neighborhood ?? '',
    cidade: corpo.city ?? '',
    uf: corpo.state ?? '',
  };
}
